import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography, Chip, Grid, Button, CircularProgress, Snackbar, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import ComplianceDetails from "./ComplianceDetails";

// The period a gap covers lives under a different key pair per gap type: a
// claim is stamped with its service dates, a prior auth with the authorized
// period. These are read in UTC -- they arrive as UTC midnight, so reading
// them locally would pull a period start into the previous day.
function periodBounds(row) {
    const facts = row.sql_facts || {};
    const start = facts.service_start_date || facts.auth_start_date || null;
    const end = facts.service_end_date || facts.auth_end_date || start;
    const startMs = start ? Date.parse(start) : NaN;
    if (Number.isNaN(startMs)) return null;
    const endMs = end ? Date.parse(end) : NaN;
    return { startMs, endMs: Number.isNaN(endMs) ? startMs : endMs };
}

function formatCurrency(value) {
    return Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatPeriodDate(ms) {
    const date = new Date(ms);
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${String(date.getUTCFullYear()).slice(2)}`;
}

function KpiCard({ label, value }) {
    return (
        <Paper sx={{ px: 2, py: 2, height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {value === null ? "—" : value}
            </Typography>
        </Paper>
    );
}

// evaluated: false is a gap with no task narrative, so the model was never asked --
// different from "No Verdict", which is a model call that failed or could not be read.
function verdictLabel(row) {
    const verdict = row.llm_verdict;
    if (verdict?.evaluated === false) return "Not Evaluated";
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "No Verdict";
    return verdict.justified ? "Justified" : "Not Justified";
}

function verdictColor(row) {
    const verdict = row.llm_verdict;
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "default";
    return verdict.justified ? "success" : "error";
}

const columns = [
    {
        field: "customer_name",
        headerName: "Customer",
        flex: 1,
        minWidth: 180,
        // cus_id is null when the claim/auth matched no client by task or DDD id.
        valueGetter: (value, row) => row.customer_name || (row.cus_id ? `#${row.cus_id}` : "Unmatched"),
    },
    {
        field: "gap_type",
        headerName: "Type",
        width: 120,
        valueFormatter: (value) => (value === "prior_auth" ? "Prior Auth" : "Claim"),
    },
    {
        field: "reference",
        headerName: "Reference",
        width: 150,
        // A claim by its claim id; an authorization by its number, which is what
        // the payer and the Prior Auth screen show, not the row id behind it.
        valueGetter: (value, row) => {
            if (row.gap_type === "prior_auth") {
                return row.sql_facts?.prior_auth || (row.prior_auth_id ? `#${row.prior_auth_id}` : "—");
            }
            return row.claim_id || "—";
        },
    },
    {
        field: "procedure_code",
        headerName: "Procedure",
        width: 110,
        valueGetter: (value, row) => row.sql_facts?.procedure_code || "—",
    },
    {
        field: "amount_billed",
        headerName: "Amount Billed",
        type: "number",
        width: 130,
        // A claim's own amount; for an authorization, the claims billed against it.
        valueGetter: (value, row) => row.sql_facts?.amount_billed ?? null,
        valueFormatter: (value) => (value === null || value === undefined ? "—" : formatCurrency(value)),
    },
    {
        field: "issue_codes",
        headerName: "Issues",
        flex: 1.4,
        minWidth: 220,
        renderCell: (params) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                {(params.value || []).map((code) => (
                    <Chip key={code} label={code.replaceAll("_", " ")} size="small" color="warning" />
                ))}
            </Box>
        ),
    },
    {
        field: "verdict",
        headerName: "Model Verdict",
        width: 150,
        valueGetter: (value, row) => verdictLabel(row),
        renderCell: (params) => (
            <Chip
                label={params.value}
                size="small"
                color={verdictColor(params.row)}
                variant={params.row.llm_verdict?.evaluated === false ? "outlined" : "filled"}
            />
        ),
    },
    {
        field: "period",
        headerName: "Service Period",
        width: 170,
        // Sorts on the period start, renders the whole span.
        valueGetter: (value, row) => periodBounds(row)?.startMs ?? null,
        valueFormatter: (value, row) => {
            const bounds = periodBounds(row);
            if (!bounds) return "—";
            return `${formatPeriodDate(bounds.startMs)} – ${formatPeriodDate(bounds.endMs)}`;
        },
    },
    {
        field: "first_detected_at",
        headerName: "First Detected",
        width: 180,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
    },
    {
        field: "swept_at",
        headerName: "Last Swept",
        width: 180,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
    },
];

export default function ComplianceDashboard(props) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [selected, setSelected] = useState(null);
    // Week or month, from the same date range setting as the Prior Auth screen (PreAuth.js).
    const dateRangeView = props.user?.comp_settings?.find(s => s.feature_id === 70)?.feature_value === 'week' ? 'week' : 'month';
    const [periodStart, setPeriodStart] = useState(moment().startOf(dateRangeView));
    // moment is mutable, hence the copy before endOf.
    const periodEnd = moment(periodStart).endOf(dateRangeView);
    const periodLabel = dateRangeView === 'week'
        ? `Week of ${periodStart.format("MMM D")} – ${periodEnd.format("MMM D, YYYY")}`
        : periodStart.format("MMMM YYYY");
    const periodParams = useMemo(() => (dateRangeView === 'week'
        ? { startDate: periodStart.format("YYYY-MM-DD"), endDate: moment(periodStart).endOf('week').format("YYYY-MM-DD") }
        : { month: periodStart.format("YYYY-MM") }), [periodStart, dateRangeView]);
    const [sweeping, setSweeping] = useState(false);
    const [sweepMessage, setSweepMessage] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [typeFilter, setTypeFilter] = useState("all");
    // Set synchronously on click, so a second click that lands before the
    // re-render disables the button still cannot start another sweep.
    const sweepInFlight = useRef(false);

    // The employee id picks the company for a login that belongs to more than
    // one; without it the API answers 400 EMP_ID_REQUIRED for those users.
    const empId = props.user?.emp_id;

    // The API filters to records whose period overlaps the month or week -- a
    // Sunday-to-Saturday week can straddle two months, and belongs to both.
    useEffect(() => {
        let stale = false;
        setLoading(true);
        setLoadError(null);
        axios.get(APIURL + "/compliance/gaps", {
            params: { ...periodParams, emp_id: empId },
        })
            .then((response) => {
                if (!stale) setRows(response.data?.records || []);
            })
            .catch((error) => {
                console.log(error);
                if (stale) return;
                setRows([]);
                // Shown instead of an empty grid, which would read as "no gaps".
                setLoadError(error.response?.data?.message || "Could not load quality check gaps. Try again in a moment.");
            })
            .finally(() => {
                if (!stale) setLoading(false);
            });
        return () => {
            stale = true;
        };
    }, [periodParams, empId, refreshKey]);

    // The sweep is manual-only (no scheduled job) and the API scopes it to the
    // caller's own company and the selected month or week -- see routes/compliance.js
    // POST /sweep.
    const runSweep = () => {
        if (sweepInFlight.current) return;
        sweepInFlight.current = true;
        setSweeping(true);
        setSweepMessage(null);
        axios.post(APIURL + "/compliance/sweep", { ...periodParams, empId })
            .then((response) => {
                const stats = response.data?.stats || {};
                const verdictsFailed = stats.verdictsFailed || 0;
                let text = `${periodLabel} sweep complete — ${stats.gapsFound || 0} open gap(s), ${stats.gapsCleared || 0} cleared since the last sweep`;
                if (stats.verdictsReused) {
                    text += `, ${stats.verdictsReused} unchanged`;
                }
                if (verdictsFailed) {
                    text += `. ${verdictsFailed} AI evaluation(s) failed and will be retried next sweep`;
                }
                setSweepMessage({ severity: verdictsFailed ? "warning" : "success", text: `${text}.` });
                setRefreshKey((key) => key + 1);
            })
            .catch((error) => {
                console.log(error);
                const status = error.response?.status;
                // No response, or a gateway error: the connection was dropped
                // (Azure cuts requests at about 230s) but the sweep carries on
                // server-side. A 409 means one was already running.
                if (!error.response || status === 502 || status === 504) {
                    setSweepMessage({ severity: "info", text: `The ${periodLabel} sweep is still running on the server. Refresh in a few minutes to see its results.` });
                } else if (status === 409) {
                    setSweepMessage({ severity: "info", text: error.response.data?.message });
                } else {
                    setSweepMessage({ severity: "error", text: error.response.data?.message || "Sweep failed, see console." });
                }
            })
            .finally(() => {
                sweepInFlight.current = false;
                setSweeping(false);
            });
    };

    // Any day picked in week view selects the week it falls in.
    const onDateChange = (newValue) => {
        setPeriodStart(moment(newValue).startOf(dateRangeView));
    };

    // moment is mutable, hence the copy before add.
    const shiftPeriod = (count) => onDateChange(moment(periodStart).add(count, dateRangeView));

    // Nothing has been billed for a period that has not started yet.
    const isCurrentPeriod = periodStart.isSameOrAfter(moment().startOf(dateRangeView));

    const claimsWithIssues = rows.filter((row) => row.gap_type === "claim").length;
    const authsWithIssues = rows.filter((row) => row.gap_type === "prior_auth").length;
    const notJustified = rows.filter((row) => row.llm_verdict && row.llm_verdict.justified === false).length;
    // Claim gaps only: an authorization's amount is the sum of claims that can
    // also be claim gaps themselves, so adding both would count them twice.
    const claimGapAmount = rows
        .filter((row) => row.gap_type === "claim")
        .reduce((sum, row) => sum + (Number(row.sql_facts?.amount_billed) || 0), 0);
    // Counts read "—" rather than 0 when the gaps could not be loaded.
    const kpi = (value) => (loadError ? null : value);

    // Narrows the grid only; the KPI cards above always cover both types.
    const typeFilters = [
        { value: "all", label: "All", count: rows.length },
        { value: "claim", label: "Claims", count: claimsWithIssues },
        { value: "prior_auth", label: "Prior Auths", count: authsWithIssues },
    ];
    const visibleRows = typeFilter === "all" ? rows : rows.filter((row) => row.gap_type === typeFilter);

    return (
        <Box sx={{ pt: 1, pb: 1, height: "calc(100vh - 150px)", display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={() => shiftPeriod(-1)}
                    >
                        <i className="fas fa-arrow-left"></i>
                    </Button>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        {dateRangeView === 'week' ? (
                            <DatePicker
                                label="Select Week"
                                value={new Date(periodStart)}
                                format="'Week of' MMM d, yyyy"
                                disableFuture
                                onChange={(newValue) => {
                                    if (newValue && !isNaN(newValue)) onDateChange(newValue);
                                }}
                                slotProps={{ textField: { size: "small", style: { width: 260, marginLeft: 20, marginRight: 20 } } }}
                            />
                        ) : (
                            <DatePicker
                                label="Select Month"
                                views={["year", "month"]}
                                value={new Date(periodStart)}
                                disableFuture
                                onChange={(newValue) => {
                                    onDateChange(newValue);
                                }}
                                slotProps={{ textField: { size: "small", style: { marginLeft: 20, marginRight: 20 } } }}
                            />
                        )}
                    </LocalizationProvider>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={() => shiftPeriod(1)}
                        disabled={isCurrentPeriod}
                    >
                        <i className="fas fa-arrow-right"></i>
                    </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={runSweep}
                        disabled={sweeping}
                        startIcon={sweeping ? <CircularProgress size={14} /> : null}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {sweeping ? "Sweeping…" : "Run Quality Check"}
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard label="Claim Gaps" value={kpi(claimsWithIssues)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard label="Prior Auth Gaps" value={kpi(authsWithIssues)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard label="Not Justified by Narrative" value={kpi(notJustified)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard label="Billed on Claim Gaps" value={kpi(formatCurrency(claimGapAmount))} />
                </Grid>
            </Grid>

            <Paper sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 1, pt: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1.5, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Quality Check Gaps
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Exceptions paired with the AI's evaluation on whether the visit narrative justifies what
                        was billed. Click a row for detail.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, ml: "auto", alignSelf: "center" }}>
                        {typeFilters.map((filter) => (
                            <Chip
                                key={filter.value}
                                label={loadError ? filter.label : `${filter.label} (${filter.count})`}
                                size="small"
                                clickable
                                color={typeFilter === filter.value ? "primary" : "default"}
                                variant={typeFilter === filter.value ? "filled" : "outlined"}
                                onClick={() => setTypeFilter(filter.value)}
                            />
                        ))}
                    </Box>
                </Box>
                {loadError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {loadError}
                    </Alert>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        rows={visibleRows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loading}
                        density="compact"
                        disableRowSelectionOnClick
                        onRowClick={(params) => setSelected(params.row)}
                        showToolbar
                    />
                </Box>
            </Paper>

            <Snackbar open={!!sweepMessage} autoHideDuration={5000} onClose={() => setSweepMessage(null)}>
                <Alert severity={sweepMessage?.severity} elevation={6} variant="filled" onClose={() => setSweepMessage(null)}>
                    {sweepMessage?.text}
                </Alert>
            </Snackbar>

            <ComplianceDetails open={!!selected} onClose={() => setSelected(null)} record={selected} />
        </Box>
    );
}
